"use client"

/*
 * START_MODULE_CONTRACT
 *   PURPOSE: Fetch and refresh the current user's active subscription
 *   PROPS:   none (hook)
 *   STATE:   subscription, loading
 *   DEPENDS: M-API-CLIENT (api)
 *   EMITS:   refresh (re-fetch after checkout/cancel)
 *   ACCESSIBILITY: n/a (data hook)
 *   LINKS:   M-USE-SUBSCRIPTION
 * END_MODULE_CONTRACT
 *
 * CHANGE_SUMMARY
 *   [2026-06-16] Initial subscription data hook (GET /api/v1/subscriptions/me)
 */

import { useCallback, useEffect, useState } from "react"
import { api } from "@/lib/api-client"

export interface Subscription {
  plan_code: string
  status: string
  started_at: string
  current_period_end: string | null
}

interface MePayload {
  subscription: Subscription | null
}

export function useSubscription() {
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    const res = await api.get("/api/v1/subscriptions/me")
    if (!res.ok) {
      setSubscription(null)
      setLoading(false)
      return
    }
    const data: MePayload = await res.json()
    setSubscription(data.subscription)
    setLoading(false)
  }, [])

  useEffect(() => {
    let cancelled = false
    async function load() {
      const res = await api.get("/api/v1/subscriptions/me")
      if (cancelled) return
      if (!res.ok) {
        setSubscription(null)
      } else {
        const data: MePayload = await res.json()
        setSubscription(data.subscription)
      }
      setLoading(false)
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  return { subscription, loading, refresh }
}
